import { isEmpty } from 'lodash'
import moment from 'moment-timezone'
import React, { useMemo, useState } from 'react'
import { Row } from 'react-table'

import { useTimeZone } from 'civic-champs-shared/auth/hooks'
import format from 'civic-champs-shared/utils/format'
import { useCurrentDateWithFilename } from 'components/table/ExportCsvMenuItem'
import { useFeatureEnabled } from 'core/feature/hooks'
import { ExportButton } from 'core/table/components'
import { DailyEventTableRow } from 'Event/components/DailyEventsTable'
import { useGetRegistrants } from 'volunteering/volunteers/hooks/useGetRegistrants'
import { RegistrantResponse } from 'Event/interfaces'

const localToMilitary = (localTime: string) => moment(localTime, 'h:mm A').format('H:mm')

const getShiftLength = (start: string, end: string) =>
  moment(end, 'h:mm A').diff(moment(start, 'h:mm A'), 'hours', true)

const filename = 'daily-events.csv'

const headersWithoutRegistrations = [
  { label: 'Event Name', key: 'name' },
  { label: 'Event Start', key: 'startsAt' },
  { label: 'Event End', key: 'endsAt' },
  { label: 'Registered Volunteers', key: 'registrations.confirmed' },
  { label: 'Total Required Volunteers', key: 'registrations.total' },
]

const headersWithRegistrations = [
  { label: 'Event Name', key: 'event.name' },
  { label: 'Role', key: 'role' },
  { label: 'Event Date', key: 'event.date' },
  { label: 'Event Start Time', key: 'event.start' },
  { label: 'Event End Time', key: 'event.end' },
  { label: 'Event Duration (Hours)', key: 'event.duration' },
  { label: 'Registered Volunteers', key: 'event.registrations.confirmed' },
  { label: 'Maximum Registrants', key: 'event.registrations.total' },
  { label: 'Volunteer First Name', key: 'volunteer.givenName' },
  { label: 'Volunteer Last Name', key: 'volunteer.familyName' },
]

// "original" formatData function
const formatDataWithoutRegistrations = (events: Row<DailyEventTableRow>[]) => {
  return events.map(({ original: { startsAt, endsAt, registrations, name } }) => {
    return {
      name,
      startsAt: format.datetimeForExport(startsAt),
      endsAt: format.datetimeForExport(endsAt),
      registrations,
    }
  })
}

const useFormatData = () => {
  const includeRegistrations = useFeatureEnabled('RegistrationsInListOfEventsExport')
  const tz = useTimeZone()

  const [getRegistrants] = useGetRegistrants()
  const [busy, setBusy] = useState(false)

  const [headers, formatData] = useMemo((): [any[], (events: Row<DailyEventTableRow>[]) => any] => {
    if (includeRegistrations) {
      const formatData = async (events: Row<DailyEventTableRow>[]) => {
        const registrantsByQueryKey = new Map<string, RegistrantResponse[]>()
        const results: any[] = []
        setBusy(true)

        try {
          for (const { original } of events) {
            const date = moment.tz(original.startsAt, tz).format('MM/DD/YYYY')
            const queryKey = `${original.occurrenceId}_${date}`

            let registrants: RegistrantResponse[]
            if (registrantsByQueryKey.has(queryKey)) {
              registrants = registrantsByQueryKey.get(queryKey) ?? []
            } else {
              if (original.registrations.confirmed === 0) {
                registrants = []
              } else {
                registrants = await getRegistrants({
                  occurrenceId: original.occurrenceId,
                  date: original.startsAt,
                })
              }

              registrantsByQueryKey.set(queryKey, registrants)
            }

            const start = moment.tz(original.startsAt, tz).format('h:mm a')
            const end = moment.tz(original.endsAt, tz).format('h:mm a')

            if (isEmpty(registrants)) {
              results.push({
                event: {
                  name: original.name,
                  date,
                  start: localToMilitary(start),
                  end: localToMilitary(end),
                  duration: getShiftLength(start, end),
                  registrations: original.registrations,
                },
                role: original.roleName,
              })
            } else {
              registrants
                .filter(registrant => registrant.role.id === original.roleId)
                .forEach(registrant => {
                  results.push({
                    event: {
                      name: original.name,
                      date,
                      start: localToMilitary(start),
                      end: localToMilitary(end),
                      duration: getShiftLength(start, end),
                      registrations: original.registrations,
                    },
                    role: registrant.role.name,
                    volunteer: registrant.user,
                  })
                })
            }
          }
        } finally {
          setBusy(false)
        }

        return results
      }

      return [headersWithRegistrations, formatData]
    } else {
      return [headersWithoutRegistrations, formatDataWithoutRegistrations]
    }
  }, [getRegistrants, includeRegistrations, tz])

  return { headers, formatData, busy }
}

const ExportDailyEventsButton = ({ data, disabled }: { data: any[]; disabled?: boolean }) => {
  const fileNameWithDate = useCurrentDateWithFilename(filename)
  const { headers, formatData, busy } = useFormatData()

  return (
    <ExportButton
      data={data}
      formatData={formatData}
      headers={headers}
      disabled={disabled || busy}
      filename={fileNameWithDate as string}
      type="popover"
    />
  )
}

export default ExportDailyEventsButton
